/**
 * NoteContent — renders a Nostr note's plaintext content with:
 *  - Image URLs (jpg/jpeg/png/gif/webp/avif/svg) rendered as inline images
 *  - Plain URLs rendered as clickable links
 *  - Everything else rendered as plain text (whitespace preserved)
 */

import { useMemo } from "react";

const IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?[^\s]*)?$/i;

// Matches http/https URLs
const URL_REGEX = /https?:\/\/[^\s<>"']+/g;

type Segment =
  | { type: "text"; value: string }
  | { type: "image"; url: string }
  | { type: "link"; url: string };

function parseContent(content: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(URL_REGEX)) {
    const url = match[0];
    const start = match.index!;

    // Push preceding text
    if (start > lastIndex) {
      segments.push({ type: "text", value: content.slice(lastIndex, start) });
    }

    if (IMAGE_EXTS.test(url)) {
      segments.push({ type: "image", url });
    } else {
      segments.push({ type: "link", url });
    }

    lastIndex = start + url.length;
  }

  // Remaining text
  if (lastIndex < content.length) {
    segments.push({ type: "text", value: content.slice(lastIndex) });
  }

  return segments;
}

interface NoteContentProps {
  content: string;
  className?: string;
}

export function NoteContent({ content, className }: NoteContentProps) {
  const segments = useMemo(() => parseContent(content), [content]);

  return (
    <div className={className}>
      <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
        {segments.map((seg, i) => {
          if (seg.type === "image") {
            return null; // images are rendered below, not inline in the text
          }
          if (seg.type === "link") {
            return (
              <a
                key={i}
                href={seg.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 dark:text-amber-400 underline underline-offset-2 break-all hover:text-amber-700 dark:hover:text-amber-300"
              >
                {seg.url}
              </a>
            );
          }
          return <span key={i}>{seg.value}</span>;
        })}
      </p>

      {/* Inline images rendered below the text */}
      {segments.filter((s) => s.type === "image").length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {segments
            .filter(
              (s): s is Extract<Segment, { type: "image" }> =>
                s.type === "image",
            )
            .map((seg, i) => (
              <a
                key={i}
                href={seg.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg overflow-hidden border border-border max-w-full"
              >
                <img
                  src={seg.url}
                  alt=""
                  loading="lazy"
                  className="max-h-80 max-w-full object-contain rounded-lg"
                />
              </a>
            ))}
        </div>
      )}
    </div>
  );
}
